import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import { formatPKR } from '../utils/currencyFormatter';
import { formatPartySearchBalanceLabel, partySearchBalanceClassName } from '../utils/partySearchBalance';
import { getErrorMessage } from '../utils/errorHandler';
import { API } from '../config';
import axios from 'axios';
import { getSaleItemProductCode } from '../utils/saleInvoiceDisplay';
import { HiPlus, HiBan, HiArrowLeft } from 'react-icons/hi';
import './PointOfSale.css';

const POS_CART_STORAGE_KEY = 'pos_cart_state';

const TAB_ALL = 'all';
const TAB_OUR_CREATIONS = 'our_creations';
const TAB_STITCHED = 'stitched';
const TAB_UNSTITCHED = 'unstitched';

const PointOfSale = () => {
  const { user } = useAuth();
  const { effectiveBranchId, hasBranches, isAdmin } = useBranch();
  const navigate = useNavigate();
  const { success, error } = useNotification();

  const [activeTab, setActiveTab] = useState(TAB_ALL);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountLoading, setAccountLoading] = useState(false);

  const [cart, setCart] = useState([]);
  const [orderDiscount, setOrderDiscount] = useState(0);
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [accountId, setAccountId] = useState('');
  const [isWalkIn, setIsWalkIn] = useState(null); // null = not chosen yet
  const [paidAmount, setPaidAmount] = useState('');
  const [paymentSplits, setPaymentSplits] = useState([{ paymentMethod: 'cash', accountId: '', amount: '' }]);

  const [variantModalProduct, setVariantModalProduct] = useState(null);
  const [variantQtys, setVariantQtys] = useState({});
  const [variantPrices, setVariantPrices] = useState({});
  const [variantErrors, setVariantErrors] = useState({});

  const [charging, setCharging] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user?.business_id || user?.businessId || '');
  const [businesses, setBusinesses] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [discountMode, setDiscountMode] = useState('amount'); // 'amount' | 'percent'
  const [editingCartIndex, setEditingCartIndex] = useState(null);
  const [taxPercent, setTaxPercent] = useState(0);
  const [showTaxInput, setShowTaxInput] = useState(false);
  const [showReturnSaleModal, setShowReturnSaleModal] = useState(false);
  const [returnSaleList, setReturnSaleList] = useState([]);
  const [returnSaleLoading, setReturnSaleLoading] = useState(false);
  const [returnSaleSearch, setReturnSaleSearch] = useState('');
  const getTodayStr = () => new Date().toISOString().split('T')[0];
  const [saleDate, setSaleDate] = useState(() => new Date().toISOString().split('T')[0]);
  const hasRestoredCartRef = useRef(false);
  const prevIsWalkInRef = useRef(isWalkIn);

  const businessId = user?.role === 'super_admin' ? selectedBusinessId : (user?.business_id || user?.businessId);

  useEffect(() => {
    if (!effectiveBranchId) return;
    try {
      const raw = localStorage.getItem(POS_CART_STORAGE_KEY);
      if (!raw) return;
      const all = JSON.parse(raw);
      const data = all && typeof all === 'object' && all[String(effectiveBranchId)];
      if (!data || !Array.isArray(data.cart)) return;
      setCart(data.cart);
      if (data.orderDiscount != null) setOrderDiscount(data.orderDiscount);
      if (data.discountMode != null) setDiscountMode(data.discountMode);
      if (data.taxPercent != null) setTaxPercent(data.taxPercent);
      if (data.customerId != null) setCustomerId(data.customerId);
      if (data.customerName != null) setCustomerName(data.customerName);
      if (data.customerPhone != null) setCustomerPhone(data.customerPhone);
      if (data.isWalkIn != null) setIsWalkIn(data.isWalkIn);
      if (data.accountId != null) setAccountId(String(data.accountId));
      if (data.paidAmount != null) setPaidAmount(data.paidAmount === undefined || data.paidAmount === '' ? '' : String(data.paidAmount));
      if (data.paymentSplits != null && Array.isArray(data.paymentSplits)) setPaymentSplits(data.paymentSplits.length ? data.paymentSplits : [{ paymentMethod: 'cash', accountId: '', amount: '' }]);
    } catch (e) {
      // ignore
    } finally {
      hasRestoredCartRef.current = true;
    }
  }, [effectiveBranchId]);

  useEffect(() => {
    if (!effectiveBranchId || !hasRestoredCartRef.current) return;
    try {
      const raw = localStorage.getItem(POS_CART_STORAGE_KEY);
      const all = raw ? (() => { try { return JSON.parse(raw); } catch (e) { return {}; } })() : {};
      all[String(effectiveBranchId)] = {
        branchId: effectiveBranchId,
        cart,
        orderDiscount,
        discountMode,
        taxPercent,
        customerId,
        customerName,
        customerPhone,
        isWalkIn,
        accountId: accountId || undefined,
        paidAmount: paidAmount || undefined,
        paymentSplits: paymentSplits.length ? paymentSplits : undefined
      };
      localStorage.setItem(POS_CART_STORAGE_KEY, JSON.stringify(all));
    } catch (e) {
      // ignore
    }
  }, [effectiveBranchId, cart, orderDiscount, discountMode, taxPercent, customerId, customerName, customerPhone, isWalkIn, accountId, paidAmount, paymentSplits]);

  // When switching to walk-in: collapse payment to single-account; clear paidAmount display (walk-in always pays full)
  useEffect(() => {
    if (isWalkIn === true) {
      setPaidAmount('');
      setPaymentSplits([{ paymentMethod: 'cash', accountId: '', amount: '' }]);
    }
  }, [isWalkIn]);

  // When switching to registered customer, default paid to total once; when total drops, clamp paidAmount
  useEffect(() => {
    const justSwitchedToCustomer = prevIsWalkInRef.current === true && isWalkIn === false;
    prevIsWalkInRef.current = isWalkIn;
    if (isWalkIn !== false) return;
    const r2 = (n) => Number(Number(n).toFixed(2));
    const st = r2(cart.reduce((sum, item) => sum + r2((item.quantity * item.unitPrice) - (item.discount || 0)), 0));
    const raw = parseFloat(orderDiscount) || 0;
    const pct = discountMode === 'percent' ? Math.min(100, Math.max(0, raw)) : 0;
    const disc = r2(discountMode === 'percent' ? (st > 0 ? (st * pct) / 100 : 0) : raw);
    const after = r2(Math.max(0, st - disc));
    const tp = Math.min(100, Math.max(0, parseFloat(taxPercent) || 0));
    const tax = r2(after > 0 ? (after * tp) / 100 : 0);
    const tot = r2(Math.max(0, after + tax));
    if (justSwitchedToCustomer && tot > 0) setPaidAmount(String(tot));
    else {
      const paid = parseFloat(paidAmount) || 0;
      if (tot > 0 && paid > tot) setPaidAmount(String(tot));
    }
  }, [isWalkIn, paidAmount, cart, orderDiscount, discountMode, taxPercent]);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      axios.get(`${API}/businesses/all`)
        .then((res) => {
          const list = res.data.businesses || [];
          setBusinesses(list);
          if (!selectedBusinessId && list.length > 0) setSelectedBusinessId(list[0].id);
        })
        .catch(() => setBusinesses([]));
    }
  }, [user?.role]);

  const fetchProducts = useCallback(async () => {
    if (!businessId || !effectiveBranchId) return;
    setProductsLoading(true);
    try {
      let url = `${API}/products/business/${businessId}?branchId=${effectiveBranchId}`;
      if (activeTab === TAB_OUR_CREATIONS) url += '&ourCreations=true';
      else if (activeTab === TAB_STITCHED) url += '&productType=stitched';
      else if (activeTab === TAB_UNSTITCHED) url += '&productType=unstitched';
      const res = await axios.get(url);
      setProducts(res.data.products || []);
    } catch (err) {
      console.error('Failed to fetch products', err);
      setProducts([]);
      error(getErrorMessage(err));
    } finally {
      setProductsLoading(false);
    }
  }, [businessId, effectiveBranchId, activeTab, error]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (!businessId || !effectiveBranchId) return;
    let cancelled = false;
    axios.get(`${API}/parties/business/${businessId}/customers?branchId=${effectiveBranchId}`)
      .then((res) => { if (!cancelled) setCustomers(res.data.customers || []); })
      .catch(() => { if (!cancelled) setCustomers([]); });
    return () => { cancelled = true; };
  }, [businessId, effectiveBranchId]);

  useEffect(() => {
    if (!showReturnSaleModal || !businessId) return;
    setReturnSaleLoading(true);
    const params = new URLSearchParams();
    if (effectiveBranchId) params.set('branchId', effectiveBranchId);
    const url = `${API}/sales/business/${businessId}${params.toString() ? `?${params.toString()}` : ''}`;
    axios.get(url)
      .then((res) => setReturnSaleList(res.data.sales || []))
      .catch(() => setReturnSaleList([]))
      .finally(() => setReturnSaleLoading(false));
  }, [showReturnSaleModal, businessId, effectiveBranchId]);

  useEffect(() => {
    if (!businessId || !effectiveBranchId) return;
    setAccountLoading(true);
    axios.get(`${API}/funds/business/${businessId}/accounts?branchId=${effectiveBranchId}`)
      .then((res) => {
        const accs = res.data.accounts || [];
        setAccounts(accs);
        if (accs.length > 0 && !accountId) {
          const cash = accs.find((a) => (a.type || a.account_type) === 'cash');
          setAccountId(String((cash || accs[0]).id));
        }
      })
      .catch(() => setAccounts([]))
      .finally(() => setAccountLoading(false));
  }, [businessId, effectiveBranchId]);

  const getProductStock = (p) => {
    const q = p.display_quantity ?? p.displayQuantity ?? p.quantity ?? 0;
    return parseFloat(q) || 0;
  };

  /** All products for the current branch (including zero stock); add-to-cart disabled when stock is 0 */
  const hasVariants = (p) => (p.variants?.length > 0);

  const openVariantModal = (product) => {
    const stock = getProductStock(product);
    if (stock <= 0) {
      error('This product is out of stock');
      return;
    }
    const vq = {};
    const vp = {};
    (product.variants || []).forEach((v) => {
      vq[v.id] = 0;
      const variantCost =
        v.cost_price ??
        v.costPrice ??
        product.cost_price ??
        product.costPrice ??
        0;
      vp[v.id] = String(variantCost || 0);
    });
    setVariantQtys(vq);
    setVariantPrices(vp);
    setVariantModalProduct(product);
  };

  const addProductToCart = (product, quantity = 1, variantId = null, variantLabel = '') => {
    const stock = getProductStock(product);
    if (!variantId && product.variants?.length > 0) {
      openVariantModal(product);
      return;
    }
    if (stock <= 0) {
      error('This product is out of stock');
      return;
    }
    const qty = Math.min(quantity, stock);
    const unitPrice = parseFloat(product.sale_price ?? product.cost_price ?? 0) || 0;
    const name = product.name + (variantLabel ? ` — ${variantLabel}` : '');
    setCart((prev) => [
      ...prev,
      {
        productId: product.id,
        variantId: variantId || product.variants?.[0]?.id || null,
        productCode: product.product_code || product.productCode || '',
        productName: name,
        quantity: qty,
        unitPrice,
        discount: 0
      }
    ]);
  };

  const addVariantsToCart = () => {
    if (!variantModalProduct) return;

    // Do not proceed while there are validation errors on variant quantities
    if (Object.values(variantErrors).some(Boolean)) {
      error('Please fix the quantities highlighted in red before adding to the sale.');
      return;
    }

    const newItems = [];
    (variantModalProduct.variants || []).forEach((v) => {
      const qty = parseFloat(variantQtys[v.id]) || 0;
      if (qty <= 0) return;
      const avail = parseFloat(v.available_quantity ?? v.availableQuantity ?? 0) || 0;
      if (qty > avail) return;
      const variantSalePrice = (() => {
        const fromState = variantPrices[v.id];
        if (fromState !== undefined && fromState !== '') {
          return parseFloat(fromState) || 0;
        }
        return parseFloat(
          v.sale_price ??
          v.salePrice ??
          v.cost_price ??
          v.costPrice ??
          variantModalProduct.cost_price ??
          variantModalProduct.costPrice ??
          0
        ) || 0;
      })();
      newItems.push({
        productId: variantModalProduct.id,
        variantId: v.id,
        productCode: variantModalProduct.product_code || variantModalProduct.productCode || '',
        productName: `${variantModalProduct.name} — ${[v.size, v.color, v.sku].filter(Boolean).filter(x => String(x).toLowerCase() !== 'default').join(' / ') || 'Variant'}`,
        quantity: qty,
        unitPrice: variantSalePrice,
        discount: 0
      });
    });
    if (newItems.length === 0) {
      error('Enter quantity for at least one size');
      return;
    }
    setCart((prev) => [...prev, ...newItems]);
    setVariantModalProduct(null);
    setVariantQtys({});
    setVariantErrors({});
  };

  const removeCartItem = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
    if (editingCartIndex === index) setEditingCartIndex(null);
    else if (editingCartIndex > index) setEditingCartIndex((prev) => prev - 1);
  };

  const round2 = (n) => Number(Number(n).toFixed(2));
  const lineTotal = (item) => round2((item.quantity * item.unitPrice) - (item.discount || 0));
  const subtotal = round2(cart.reduce((sum, item) => sum + lineTotal(item), 0));
  const rawDiscount = parseFloat(orderDiscount) || 0;
  const percent = discountMode === 'percent' ? Math.min(100, Math.max(0, rawDiscount)) : 0;
  const discountAmount = round2(
    discountMode === 'percent'
      ? (subtotal > 0 ? (subtotal * percent) / 100 : 0)
      : rawDiscount
  );
  const afterDiscount = round2(Math.max(0, subtotal - discountAmount));
  const taxPct = Math.min(100, Math.max(0, parseFloat(taxPercent) || 0));
  const taxAmount = round2(afterDiscount > 0 ? (afterDiscount * taxPct) / 100 : 0);
  const total = round2(Math.max(0, afterDiscount + taxAmount));

  // Payment validation for registered customers (credit + split)
  const paid = isWalkIn ? total : (parseFloat(paidAmount) || 0);
  const allocated = paymentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const debt = round2(Math.max(0, total - paid));
  const unallocated = round2(Math.max(0, paid - allocated));
  const singleSplitNoAmount = paymentSplits.length === 1 && paymentSplits[0].accountId && (paymentSplits[0].amount === '' || paymentSplits[0].amount == null);
  const splitMatchesPaid = paid <= 0 || Math.abs(allocated - paid) < 0.01;
  // Walk-in: every split that has an amount must have an account selected
  const allSplitsWithAmountHaveAccount = paymentSplits.every(s => (parseFloat(s.amount) || 0) <= 0 || (s.accountId && String(s.accountId).trim() !== ''));
  const canChargeWalkIn = cart.length > 0 && (total <= 0 || singleSplitNoAmount || (splitMatchesPaid && allSplitsWithAmountHaveAccount && paymentSplits.some(s => s.accountId && (parseFloat(s.amount) || 0) > 0)));
  const canChargeCustomer = cart.length > 0 && (
    paid === 0 ||
    (paid > 0 && (singleSplitNoAmount || (splitMatchesPaid && paymentSplits.some(s => s.accountId && (parseFloat(s.amount) || 0) > 0))))
  );
  const chargeDisabled =
    charging ||
    (isWalkIn == null
      ? true
      : (isWalkIn ? !canChargeWalkIn : !canChargeCustomer));

  const handleCharge = async () => {
    if (cart.length === 0) {
      error('Add at least one item to the sale');
      return;
    }
    if (isWalkIn == null) {
      error('Select Walk-in or Customer before charging.');
      return;
    }
    if (!isWalkIn && !customerId) {
      error('Select a registered customer before charging.');
      return;
    }
    if (isWalkIn && total > 0) {
      if (!singleSplitNoAmount && !splitMatchesPaid) {
        error('Split total must equal sale total. Allocate amounts across accounts.');
        return;
      }
      if (!singleSplitNoAmount && !paymentSplits.some(s => s.accountId && (parseFloat(s.amount) || 0) > 0)) {
        error('Select an account (or add split lines with account and amount).');
        return;
      }
      if (!singleSplitNoAmount && !allSplitsWithAmountHaveAccount) {
        error('Select an account for every payment line that has an amount.');
        return;
      }
    }
    if (isWalkIn && total > 0 && paid < total - 0.01) {
      error('Walk-in customers must pay in full (no credit).');
      return;
    }
    if (!isWalkIn && paid > 0) {
      if (singleSplitNoAmount && !paymentSplits[0].accountId) {
        error('Select an account to deposit to, or add split lines with amounts.');
        return;
      }
      if (!singleSplitNoAmount && !splitMatchesPaid) {
        error('Split total must equal amount received. Allocate or adjust amounts.');
        return;
      }
      if (!singleSplitNoAmount && !paymentSplits.some(s => s.accountId && (parseFloat(s.amount) || 0) > 0)) {
        error('Add at least one deposit line with account and amount.');
        return;
      }
    }
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch to use POS.');
      return;
    }
    if (!businessId) {
      error('Business context is missing.');
      return;
    }

    setCharging(true);
    try {
      const selectedCustomer = !isWalkIn && customerId ? customers.find((c) => String(c.id) === customerId) : null;
      const paidVal = isWalkIn ? total : (parseFloat(paidAmount) || 0);
      let paymentSplitsPayload = null;
      if (paidVal > 0) {
        if (singleSplitNoAmount && paymentSplits[0].accountId) {
          paymentSplitsPayload = [{ accountId: parseInt(paymentSplits[0].accountId, 10), amount: paidVal }];
        } else {
          const validSplits = paymentSplits.filter(s => s.accountId && (parseFloat(s.amount) || 0) > 0);
          if (validSplits.length > 0) {
            paymentSplitsPayload = validSplits.map(s => ({ accountId: parseInt(s.accountId, 10), amount: parseFloat(s.amount) }));
          }
        }
      }
      const payload = {
        branchId: parseInt(effectiveBranchId, 10),
        businessId: parseInt(businessId, 10),
        customerId: isWalkIn ? null : (customerId ? parseInt(customerId, 10) : null),
        customerName: isWalkIn ? (customerName || null) : (selectedCustomer?.name || null),
        customerPhone: isWalkIn ? (customerPhone || null) : (selectedCustomer?.phone || null),
        paymentMethod: isWalkIn ? 'cash' : (paymentSplits[0]?.paymentMethod || 'cash'),
        discount: discountAmount,
        tax: taxAmount,
        paidAmount: paidVal,
        accountId: !paymentSplitsPayload && accountId ? parseInt(accountId, 10) : null,
        paymentSplits: paymentSplitsPayload || undefined,
        notes: '',
        saleDate: saleDate || getTodayStr(),
        items: cart.map((item) => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount || 0
        }))
      };

      const createRes = await axios.post(`${API}/sales`, payload);
      const sale = createRes.data.sale || createRes.data;
      const saleId = sale.id;

      await axios.post(`${API}/sales/${saleId}/confirm`);
      success('Sale completed!');

      const invRes = await axios.get(`${API}/sales/${saleId}/invoice`);
      setInvoiceModal(invRes.data.invoice || invRes.data);

      setCart([]);
      setOrderDiscount(0);
      setTaxPercent(0);
      setShowTaxInput(false);
      setPaidAmount('');
      setPaymentSplits([{ paymentMethod: 'cash', accountId: '', amount: '' }]);
      if (isWalkIn) {
        setCustomerName('');
        setCustomerPhone('');
      }
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setCharging(false);
    }
  };

  const closeInvoiceModal = () => {
    setInvoiceModal(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const filteredCustomers = customerSearch.trim()
    ? customers.filter((c) => {
        const q = customerSearch.trim().toLowerCase();
        const name = (c.name || '').toLowerCase();
        const phone = (c.phone || '').replace(/\s/g, '');
        const city = (c.city || '').toLowerCase();
        return name.includes(q) || phone.includes(q) || city.includes(q);
      }).slice(0, 15)
    : customers.slice(0, 15);

  if (isAdmin && hasBranches && !effectiveBranchId) {
    return (
      <Layout>
        <div className="pos-page">
          <div className="pos-no-branch">
            <p>Select a branch from the header dropdown to use Point of Sale.</p>
            <button type="button" className="pos-btn-secondary" onClick={() => navigate('/sales')}>
              Go to Sales
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pos-page">
        <div className="pos-layout">
          <section className="pos-left">
            <div className="pos-title-row">
              <h1 className="pos-title">Point of Sale</h1>
              <button
                type="button"
                className="pos-btn-return-sale"
                onClick={() => { setReturnSaleSearch(''); setShowReturnSaleModal(true); }}
                title="Return sale"
              >
                <HiArrowLeft /> Return sale
              </button>
            </div>
            {user?.role === 'super_admin' && businesses.length > 0 && (
              <div className="pos-business-row">
                <label htmlFor="pos-business">Business</label>
                <select
                  id="pos-business"
                  value={selectedBusinessId}
                  onChange={(e) => setSelectedBusinessId(e.target.value)}
                  className="pos-select pos-business-select"
                >
                  {businesses.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="pos-tabs">
              {[
                [TAB_ALL, 'All'],
                [TAB_OUR_CREATIONS, 'Our Creations'],
                [TAB_STITCHED, 'Stitched'],
                [TAB_UNSTITCHED, 'Unstitched']
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`pos-tab ${activeTab === key ? 'active' : ''}`}
                  onClick={() => setActiveTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="pos-search-wrap">
              <input
                type="text"
                placeholder="Search by product name or code..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="pos-search-input"
                aria-label="Search products"
              />
            </div>

            {productsLoading ? (
              <Loader message="Loading products…" variant="block" />
            ) : (() => {
              const q = (productSearchTerm || '').trim().toLowerCase();
              const filtered = q
                ? products.filter((p) => {
                    const name = (p.name || '').toLowerCase();
                    const code = (p.product_code || p.productCode || '').toLowerCase();
                    return name.includes(q) || code.includes(q);
                  })
                : products;
              return filtered.length === 0 ? (
                <div className="pos-empty">
                  {products.length === 0
                    ? 'No products in this branch. Add or transfer stock, or select another branch.'
                    : 'No products match your search. Try a different name or code.'}
                </div>
              ) : (
              <div className="pos-product-grid">
                {filtered.map((product) => {
                  const stock = getProductStock(product);
                  const canAdd = stock > 0;
                  return (
                    <button
                      key={product.id}
                      type="button"
                      className={`pos-product-card ${!canAdd ? 'out-of-stock' : ''}`}
                      onClick={() => addProductToCart(product)}
                      disabled={!canAdd}
                    >
                      <div className="pos-product-image">
                        {product.image_url || product.imageUrl ? (
                          <img src={product.image_url || product.imageUrl} alt={product.name} />
                        ) : (
                          <div className="pos-product-placeholder">
                            {product.product_code || product.productCode || '—'}
                          </div>
                        )}
                      </div>
                      <div className="pos-product-info">
                        {activeTab !== TAB_ALL && (
                          <span className="pos-product-code">{product.product_code || product.productCode || '—'}</span>
                        )}
                        <span className="pos-product-name">{product.name}</span>
                        {activeTab === TAB_ALL && (
                          <span className="pos-product-type">
                            {(product.product_type || product.productType) === 'unstitched' || product.unit_type === 'guz'
                              ? 'Unstitched'
                              : 'Stitched'}
                          </span>
                        )}
                        <span className="pos-product-price">
                          {product.sale_price != null
                            ? <>Sale: {formatPKR(product.sale_price)}</>
                            : <>Price: {formatPKR(product.cost_price ?? 0)}</>}
                        </span>
                        {product.cost_price != null && (
                          <span className="pos-product-cost">
                            Cost: {formatPKR(product.cost_price)}
                          </span>
                        )}
                        <span className="pos-product-stock">Stock: {product.unit_type === 'guz' ? stock.toFixed(2) : stock}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              );
            })()}
          </section>

          <aside className="pos-right">
            <div className="pos-cart-header">
              <h2>Current sale ({cart.length})</h2>
            </div>

            <div className="pos-customer">
              <div className="pos-customer-current">
                <span className="pos-customer-current-label">Customer:</span>
                <strong className="pos-customer-current-name">
                  {isWalkIn ? (customerName?.trim() || 'Walk-in') : (customerId ? (customers.find((c) => String(c.id) === customerId)?.name || 'Customer') : '—')}
                </strong>
              </div>
              {!isWalkIn && customerId && (() => {
                const sel = customers.find((c) => String(c.id) === customerId);
                if (!sel) return null;
                return (
                  <div className="pos-selected-customer-card">
                    <div className="pos-selected-customer-card-name">{sel.name}</div>
                    {(sel.city || sel.phone) && (
                      <div className="pos-selected-customer-card-details">
                        {sel.phone && <span className="pos-selected-customer-card-phone">{sel.phone}</span>}
                        {sel.phone && sel.city && <span className="pos-selected-customer-card-sep"> · </span>}
                        {sel.city && <span className="pos-selected-customer-card-city">{sel.city}</span>}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="pos-customer-toggle">
                <button
                  type="button"
                  className={isWalkIn ? 'active' : ''}
                  onClick={() => { setIsWalkIn(true); setCustomerId(''); setCustomerSearch(''); }}
                >
                  Walk-in
                </button>
                <button
                  type="button"
                  className={!isWalkIn ? 'active' : ''}
                  onClick={() => setIsWalkIn(false)}
                >
                  Customer
                </button>
              </div>
              {isWalkIn ? (
                <div className="pos-walkin-fields">
                  <input
                    type="text"
                    placeholder="Name (optional)"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="pos-input"
                  />
                  <input
                    type="text"
                    placeholder="Phone (optional)"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="pos-input"
                  />
                </div>
              ) : (
                <div className="pos-customer-search">
                  <label className="pos-customer-label">Search customer</label>
                  <input
                    type="text"
                    placeholder="Search by name, phone, or city..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="pos-input"
                  />
                  {customerId && (
                    <div className="pos-selected-customer">
                      {customers.find((c) => String(c.id) === customerId)?.name || 'Selected'}
                      <button type="button" onClick={() => { setCustomerId(''); setCustomerSearch(''); }}>Change</button>
                    </div>
                  )}
                  {!customerId && (
                    <ul className="pos-customer-list">
                      {filteredCustomers.length === 0 ? (
                        <li className="pos-customer-list-empty">No matching customers</li>
                      ) : (
                        filteredCustomers.map((c) => (
                          <li
                            key={c.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => { setCustomerId(String(c.id)); setCustomerSearch(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && (setCustomerId(String(c.id)), setCustomerSearch(''))}
                          >
                            <div className="pos-customer-list-primary">
                              <strong className="pos-customer-name">{c.name}</strong>
                            </div>
                            <div className="pos-customer-list-meta">
                              {[c.phone, c.city].filter(Boolean).join(' · ')}
                              {(c.phone || c.city) && ' · '}
                              <span className={partySearchBalanceClassName(c.balance, c.is_shop_expense)}>
                                Balance: {formatPartySearchBalanceLabel(c.balance, c.is_shop_expense)}
                              </span>
                            </div>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="pos-cart-items">
              {cart.length === 0 ? (
                <p className="pos-cart-empty">Cart is empty. Tap a product to add.</p>
              ) : (
                <ul>
                  {cart.map((item, index) => (
                    <li key={index} className="pos-cart-item">
                      <div className="pos-cart-item-detail">
                        <div className="pos-cart-item-main">
                          <span className="pos-cart-item-name">
                            {item.productCode && <span className="pos-cart-item-code">{item.productCode}</span>}
                            <span className="pos-cart-item-name-text">{item.productName}</span>
                            <span className="pos-cart-item-meta">Qty: {item.quantity}</span>
                          </span>
                          <span className="pos-cart-item-line-total">
                            {formatPKR(item.quantity * item.unitPrice)}
                          </span>
                        </div>
                        {editingCartIndex === index && (
                          <div className="pos-cart-item-edit-row">
                            <div className="pos-cart-edit-field">
                              <label>Qty</label>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={item.quantity}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1);
                                  setCart((prev) => prev.map((it, i) => i === index ? { ...it, quantity: val === '' ? 1 : val } : it));
                                }}
                                className="pos-input pos-input-sm"
                              />
                            </div>
                            <div className="pos-cart-edit-field">
                              <label>Price</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unitPrice}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? '' : Math.max(0, parseFloat(e.target.value) || 0);
                                  setCart((prev) => prev.map((it, i) => i === index ? { ...it, unitPrice: val === '' ? 0 : val } : it));
                                }}
                                className="pos-input pos-input-sm"
                              />
                            </div>
                            <div className="pos-cart-edit-actions">
                              <button type="button" className="pos-btn-secondary pos-btn-tiny" onClick={() => setEditingCartIndex(null)}>Cancel</button>
                              <button type="button" className="pos-btn-primary pos-btn-tiny" onClick={() => setEditingCartIndex(null)}>Done</button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="pos-cart-right-actions">
                        {editingCartIndex !== index && (
                          <button
                            type="button"
                            className="pos-edit-item-btn"
                            onClick={() => setEditingCartIndex(index)}
                            aria-label="Edit"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          type="button"
                          className="pos-remove-item"
                          onClick={() => removeCartItem(index)}
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="pos-sale-date-row">
              <label>Sale date (optional)</label>
              <input
                type="date"
                value={saleDate || getTodayStr()}
                onChange={(e) => setSaleDate(e.target.value || getTodayStr())}
                className="pos-input"
              />
              <span className="pos-sale-date-hint">For party ledger. Defaults to today.</span>
            </div>

            <div className="pos-discount-row">
              <label>Discount</label>
              <div className="pos-discount-inputs">
                <div className="pos-discount-mode">
                  <button
                    type="button"
                    className={discountMode === 'amount' ? 'active' : ''}
                    onClick={() => setDiscountMode('amount')}
                  >
                    PKR
                  </button>
                  <button
                    type="button"
                    className={discountMode === 'percent' ? 'active' : ''}
                    onClick={() => setDiscountMode('percent')}
                  >
                    %
                  </button>
                </div>
                <input
                  type="number"
                  min="0"
                  max={discountMode === 'percent' ? 100 : undefined}
                  step="0.01"
                  value={orderDiscount || ''}
                  onChange={(e) => setOrderDiscount(e.target.value)}
                  className="pos-input pos-input-sm"
                  placeholder={discountMode === 'percent' ? 'e.g. 10 for 10%' : '0'}
                />
              </div>
            </div>

            <div className="pos-tax-row">
              <label>Tax</label>
              {showTaxInput ? (
                <div className="pos-tax-input-wrap">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={taxPercent || ''}
                    onChange={(e) => setTaxPercent(e.target.value)}
                    className="pos-input pos-input-sm"
                    placeholder="e.g. 16 for 16%"
                  />
                  <span className="pos-tax-hint">% of sale (after discount)</span>
                  <button type="button" className="pos-btn-secondary pos-btn-tiny" onClick={() => setShowTaxInput(false)}>Close</button>
                </div>
              ) : (
                <button type="button" className="pos-tax-btn" onClick={() => setShowTaxInput(true)}>
                  {taxPercent > 0 ? `Tax: ${taxPercent}% (${formatPKR(taxAmount)})` : 'Tax charges'}
                </button>
              )}
            </div>

            <div className="pos-total-row">
              <span>Total</span>
              <strong>{formatPKR(total)}</strong>
            </div>
            {!isWalkIn && !customerId && total > 0 && (
              <p className="pos-customer-required-hint">
                Select a registered customer to enable charging.
              </p>
            )}

            {isWalkIn ? (
              <>
                <p className="pos-walkin-hint">Full amount required (no credit). Split deposit across accounts below.</p>
                {cart.length > 0 && (() => {
                  const remaining = Math.max(0, total - allocated);
                  const firstEmptyIdx = paymentSplits.findIndex(s => !s.amount || parseFloat(s.amount) === 0);
                  const canFillRemaining = remaining > 0.01 && firstEmptyIdx >= 0 && paymentSplits[firstEmptyIdx].accountId;
                  return (
                    <div className="pos-payment-splits-block pos-payment-splits-block-compact">
                      <div className="pos-payment-splits-header pos-payment-splits-header-compact">
                        <span className="pos-payment-splits-title">Where to deposit (total: {formatPKR(total)})</span>
                        <span className="pos-payment-splits-desc-inline">Sum = sale total. Each account once.</span>
                        {accounts.length === 0 && <span className="pos-payment-splits-no-accounts">No accounts available. Add accounts in Settings.</span>}
                        <div className="pos-payment-splits-actions">
                          {canFillRemaining && (
                            <button
                              type="button"
                              className="pos-btn-fill-remaining"
                              onClick={() => {
                                const next = [...paymentSplits];
                                next[firstEmptyIdx] = { ...next[firstEmptyIdx], amount: String(remaining) };
                                setPaymentSplits(next);
                              }}
                            >
                              Fill remaining ({formatPKR(remaining)})
                            </button>
                          )}
                          <button
                            type="button"
                            className="pos-btn-add-split"
                            onClick={() => setPaymentSplits([...paymentSplits, { paymentMethod: 'cash', accountId: '', amount: '' }])}
                          >
                            <HiPlus /> Add line
                          </button>
                        </div>
                      </div>
                      <div className="pos-payment-summary-allocated-inline pos-payment-summary-allocated-inline-block">
                        {splitMatchesPaid ? <span className="ok">Allocated: {formatPKR(allocated)} ✓</span> : <span className="warning">Allocated: {formatPKR(allocated)} ({formatPKR(remaining)} left)</span>}
                      </div>
                      {(() => {
                        const withAccountAndAmount = paymentSplits.filter(s => s.accountId && ((parseFloat(s.amount) || 0) > 0 || (paymentSplits.length === 1 && (s.amount === '' || s.amount == null))));
                        if (withAccountAndAmount.length === 0) return null;
                        return (
                          <div className="pos-payment-accounts-summary">
                            <div className="pos-payment-accounts-summary-title">Depositing to</div>
                            <ul className="pos-payment-accounts-summary-list">
                              {withAccountAndAmount.map((split, idx) => {
                                const acc = accounts.find(a => String(a.id) === String(split.accountId));
                                const amt = paymentSplits.length === 1 && (split.amount === '' || split.amount == null) ? total : (parseFloat(split.amount) || 0);
                                const baseName = acc ? (acc.name || acc.account_name || `Account ${acc.id}`) : `Account ${split.accountId}`;
                                const typeLabel = acc ? ((acc.type || acc.account_type) === 'cash' ? 'Cash' : (acc.bank_name ? acc.bank_name : 'Bank')) : '';
                                return (
                                  <li key={idx} className="pos-payment-accounts-summary-row">
                                    <span className="pos-payment-accounts-summary-account">{baseName}</span>
                                    {typeLabel && <span className="pos-payment-accounts-summary-type">{typeLabel}</span>}
                                    <span className="pos-payment-accounts-summary-amount">{formatPKR(amt)}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        );
                      })()}
                      <div className="pos-payment-splits-row pos-payment-splits-header-row">
                        <span className="pos-payment-splits-col-label">Method</span>
                        <span className="pos-payment-splits-col-label">Account</span>
                        <span className="pos-payment-splits-col-label">Amount (PKR)</span>
                        <span />
                      </div>
                      <div className="pos-payment-splits-list">
                        {paymentSplits.map((split, idx) => {
                          const method = split.paymentMethod || 'cash';
                          const isCash = method === 'cash';
                          const selectedElsewhere = (id) => paymentSplits.some((s, i) => i !== idx && s.accountId === String(id));
                          const availableForRow = (acc) => String(acc.id) === split.accountId || !selectedElsewhere(acc.id);
                          const accountsByType = isCash
                            ? accounts.filter(a => (a.type || a.account_type) === 'cash').filter(availableForRow)
                            : accounts.filter(a => (a.type || a.account_type) === 'bank').filter(availableForRow);
                          return (
                            <div key={idx} className="pos-payment-split-row">
                              <select
                                className="pos-payment-split-method"
                                value={method}
                                onChange={(e) => {
                                  const next = [...paymentSplits];
                                  const newMethod = e.target.value;
                                  const wasCash = (next[idx].paymentMethod || 'cash') === 'cash';
                                  const nowCash = newMethod === 'cash';
                                  next[idx] = { ...next[idx], paymentMethod: newMethod, accountId: (wasCash === nowCash) ? next[idx].accountId : '' };
                                  setPaymentSplits(next);
                                }}
                                title="Payment method"
                              >
                                <option value="cash">Cash</option>
                                <option value="card">Card</option>
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="credit">Credit</option>
                              </select>
                              <select
                                value={split.accountId}
                                onChange={(e) => {
                                  const next = [...paymentSplits];
                                  next[idx] = { ...next[idx], accountId: e.target.value };
                                  setPaymentSplits(next);
                                }}
                                title="Deposit to account"
                              >
                                <option value="">Select account</option>
                                {accountsByType.map(acc => (
                                  <option key={acc.id} value={acc.id}>
                                    {acc.name || acc.account_name || `Account ${acc.id}`}{acc.bank_name ? ` (${acc.bank_name})` : ''} · Bal: {formatPKR(acc.current_balance ?? acc.currentBalance ?? 0)}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={split.amount}
                                onChange={(e) => {
                                  const next = [...paymentSplits];
                                  next[idx] = { ...next[idx], amount: e.target.value };
                                  setPaymentSplits(next);
                                }}
                                placeholder={paymentSplits.length === 1 ? 'Full amount' : '0'}
                                title={paymentSplits.length === 1 ? 'Enter amount or leave as Full amount' : 'Amount (PKR)'}
                                className="pos-input pos-payment-split-amount"
                              />
                              {paymentSplits.length > 1 && (
                                <button
                                  type="button"
                                  className="pos-btn-remove-split"
                                  onClick={() => setPaymentSplits(prev => { const next = prev.filter((_, i) => i !== idx); return next.length ? next : [{ paymentMethod: 'cash', accountId: '', amount: '' }]; })}
                                  aria-label="Remove"
                                >
                                  <HiBan />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {paymentSplits.length === 1 && paymentSplits[0].accountId && (
                        <p className="pos-payment-splits-hint">Leave amount empty to deposit full sale total to this account.</p>
                      )}
                    </div>
                  );
                })()}
              </>
            ) : (
              <>
                <div className="pos-payment-summary-bar pos-payment-summary-bar-compact">
                  <span className="pos-payment-summary-inline">
                    <span className="pos-payment-summary-item-inline"><span className="pos-payment-summary-label">Total</span> {formatPKR(total)}</span>
                    <span className="pos-payment-summary-arrow" aria-hidden>→</span>
                    <span className="pos-payment-summary-item-inline"><span className="pos-payment-summary-label">Received</span> {formatPKR(paid)}</span>
                    {total > 0 && (
                      <>
                        <span className="pos-payment-summary-arrow" aria-hidden>+</span>
                        <span className={`pos-payment-summary-item-inline pos-debt-inline ${debt > 0 ? 'pos-debt-visible' : ''}`}><span className="pos-payment-summary-label">Debt</span> {formatPKR(debt)}</span>
                      </>
                    )}
                  </span>
                  {paid > 0 && (
                    <span className={`pos-payment-summary-allocated-inline ${splitMatchesPaid ? 'ok' : 'warning'}`}>
                      Allocated: {formatPKR(allocated)}{splitMatchesPaid ? ' ✓' : unallocated > 0.01 ? ` (${formatPKR(unallocated)} left)` : ''}
                    </span>
                  )}
                </div>
                {paid === 0 && total > 0 && (
                  <p className="pos-payment-full-debt-hint">Full amount → customer balance (pay later).</p>
                )}
                <div className="pos-payment-paid-row pos-payment-paid-row-inline">
                  <label>Amount received (PKR)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={total}
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    placeholder="0"
                    className="pos-input pos-paid-input"
                  />
                  {total > 0 && (
                    <button type="button" className="pos-btn-quick-fill" onClick={() => setPaidAmount(String(total))}>
                      Use full total
                    </button>
                  )}
                </div>
                {paid > 0 && accounts.length > 0 && (() => {
                  const remaining = Math.max(0, paid - allocated);
                  const firstEmptyIdx = paymentSplits.findIndex(s => !s.amount || parseFloat(s.amount) === 0);
                  const canFillRemaining = remaining > 0.01 && firstEmptyIdx >= 0 && paymentSplits[firstEmptyIdx].accountId;
                  const withAccountAndAmountReg = paymentSplits.filter(s => s.accountId && ((parseFloat(s.amount) || 0) > 0 || (paymentSplits.length === 1 && (s.amount === '' || s.amount == null))));
                  return (
                    <div className="pos-payment-splits-block pos-payment-splits-block-compact">
                      <div className="pos-payment-splits-header pos-payment-splits-header-compact">
                        <span className="pos-payment-splits-title">Where to deposit</span>
                        <span className="pos-payment-splits-desc-inline">Sum = received, each account once.</span>
                        <div className="pos-payment-splits-actions">
                          {canFillRemaining && (
                            <button
                              type="button"
                              className="pos-btn-fill-remaining"
                              onClick={() => {
                                const next = [...paymentSplits];
                                next[firstEmptyIdx] = { ...next[firstEmptyIdx], amount: String(remaining) };
                                setPaymentSplits(next);
                              }}
                            >
                              Fill remaining ({formatPKR(remaining)})
                            </button>
                          )}
                          <button
                            type="button"
                            className="pos-btn-add-split"
                            onClick={() => setPaymentSplits([...paymentSplits, { paymentMethod: 'cash', accountId: '', amount: '' }])}
                          >
                            <HiPlus /> Add line
                          </button>
                        </div>
                      </div>
                      {withAccountAndAmountReg.length > 0 && (
                        <div className="pos-payment-accounts-summary">
                          <div className="pos-payment-accounts-summary-title">Depositing to</div>
                          <ul className="pos-payment-accounts-summary-list">
                            {withAccountAndAmountReg.map((split, idx) => {
                              const acc = accounts.find(a => String(a.id) === String(split.accountId));
                              const amt = paymentSplits.length === 1 && (split.amount === '' || split.amount == null) ? paid : (parseFloat(split.amount) || 0);
                              const baseName = acc ? (acc.name || acc.account_name || `Account ${acc.id}`) : `Account ${split.accountId}`;
                              const typeLabel = acc ? ((acc.type || acc.account_type) === 'cash' ? 'Cash' : (acc.bank_name ? acc.bank_name : 'Bank')) : '';
                              return (
                                <li key={idx} className="pos-payment-accounts-summary-row">
                                  <span className="pos-payment-accounts-summary-account">{baseName}</span>
                                  {typeLabel && <span className="pos-payment-accounts-summary-type">{typeLabel}</span>}
                                  <span className="pos-payment-accounts-summary-amount">{formatPKR(amt)}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                      <div className="pos-payment-splits-row pos-payment-splits-header-row">
                        <span className="pos-payment-splits-col-label">Method</span>
                        <span className="pos-payment-splits-col-label">Account</span>
                        <span className="pos-payment-splits-col-label">Amount (PKR)</span>
                        <span />
                      </div>
                      <div className="pos-payment-splits-list">
                        {paymentSplits.map((split, idx) => {
                          const method = split.paymentMethod || 'cash';
                          const isCash = method === 'cash';
                          const selectedElsewhere = (id) => paymentSplits.some((s, i) => i !== idx && s.accountId === String(id));
                          const availableForRow = (acc) => String(acc.id) === split.accountId || !selectedElsewhere(acc.id);
                          const accountsByType = isCash
                            ? accounts.filter(a => (a.type || a.account_type) === 'cash').filter(availableForRow)
                            : accounts.filter(a => (a.type || a.account_type) === 'bank').filter(availableForRow);
                          return (
                            <div key={idx} className="pos-payment-split-row">
                              <select
                                className="pos-payment-split-method"
                                value={method}
                                onChange={(e) => {
                                  const next = [...paymentSplits];
                                  const newMethod = e.target.value;
                                  const wasCash = (next[idx].paymentMethod || 'cash') === 'cash';
                                  const nowCash = newMethod === 'cash';
                                  next[idx] = { ...next[idx], paymentMethod: newMethod, accountId: (wasCash === nowCash) ? next[idx].accountId : '' };
                                  setPaymentSplits(next);
                                }}
                                title="Payment method"
                              >
                                <option value="cash">Cash</option>
                                <option value="card">Card</option>
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="credit">Credit</option>
                              </select>
                              <select
                                value={split.accountId}
                                onChange={(e) => {
                                  const next = [...paymentSplits];
                                  next[idx] = { ...next[idx], accountId: e.target.value };
                                  setPaymentSplits(next);
                                }}
                                title="Deposit to account"
                              >
                                <option value="">Select account</option>
                                {accountsByType.map(acc => (
                                  <option key={acc.id} value={acc.id}>
                                    {acc.name || acc.account_name || `Account ${acc.id}`}{acc.bank_name ? ` (${acc.bank_name})` : ''} · Bal: {formatPKR(acc.current_balance ?? acc.currentBalance ?? 0)}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={split.amount}
                                onChange={(e) => {
                                  const next = [...paymentSplits];
                                  next[idx] = { ...next[idx], amount: e.target.value };
                                  setPaymentSplits(next);
                                }}
                                placeholder={paymentSplits.length === 1 ? 'Full paid amount' : '0'}
                                title={paymentSplits.length === 1 ? 'Enter amount or leave for full received amount' : 'Amount (PKR)'}
                                className="pos-input pos-payment-split-amount"
                              />
                              {paymentSplits.length > 1 && (
                                <button
                                  type="button"
                                  className="pos-btn-remove-split"
                                  onClick={() => setPaymentSplits(prev => { const next = prev.filter((_, i) => i !== idx); return next.length ? next : [{ paymentMethod: 'cash', accountId: '', amount: '' }]; })}
                                  aria-label="Remove"
                                >
                                  <HiBan />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {paymentSplits.length === 1 && paymentSplits[0].accountId && paid > 0 && (
                        <p className="pos-payment-splits-hint">Leave amount empty for full received amount.</p>
                      )}
                    </div>
                  );
                })()}
              </>
            )}

            {(isWalkIn === true || (isWalkIn === false && customerId)) && (
              <button
                type="button"
                className="pos-charge-btn"
                onClick={handleCharge}
                disabled={chargeDisabled}
              >
                {charging ? 'Processing…' : `Charge ${formatPKR(total)}`}
              </button>
            )}

            <button type="button" className="pos-link-btn" onClick={() => navigate('/sales')}>
              View all sales
            </button>
          </aside>
        </div>
      </div>

      {/* Variant modal */}
      {variantModalProduct && (
        <div className="pos-modal-overlay" onClick={() => setVariantModalProduct(null)}>
          <div className="pos-modal pos-variant-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              Add to sale —{' '}
              {variantModalProduct.product_code || variantModalProduct.productCode ? (
                <span className="pos-variant-modal-title">
                  <span className="pos-variant-modal-code">{variantModalProduct.product_code || variantModalProduct.productCode}</span>
                  {' — '}
                  <span className="pos-variant-modal-name">{variantModalProduct.name}</span>
                </span>
              ) : (
                variantModalProduct.name
              )}
            </h3>
            <div className="pos-variant-table-wrap">
              <table className="pos-variant-table">
                <thead>
                  <tr>
                    <th>Size / Variant</th>
                    <th>Available</th>
                    <th>Cost (PKR)</th>
                    <th>Price (PKR)</th>
                    <th>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {(variantModalProduct.variants || []).map((v) => {
                    const avail = parseFloat(v.available_quantity ?? v.availableQuantity ?? 0) || 0;
                    const alreadySelected = cart.some(
                      (item) => item.productId === variantModalProduct.id && item.variantId === v.id
                    );
                    const errorMsg = variantErrors[v.id];
                    const variantCost =
                      v.cost_price ?? v.costPrice ?? variantModalProduct.cost_price ?? variantModalProduct.costPrice ?? null;
                    return (
                      <tr key={v.id}>
                        <td>{[v.size, v.color, v.sku].filter(Boolean).filter(x => String(x).toLowerCase() !== 'default').join(' / ') || '—'}</td>
                        <td>{avail}</td>
                        <td>{variantCost != null ? formatPKR(variantCost) : '—'}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={variantPrices[v.id] ?? ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setVariantPrices((prev) => ({ ...prev, [v.id]: val }));
                            }}
                            className="pos-input pos-input-sm"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max={avail}
                            step="1"
                            value={variantQtys[v.id] ?? ''}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') {
                                setVariantQtys((prev) => ({ ...prev, [v.id]: '' }));
                                setVariantErrors((prev) => ({ ...prev, [v.id]: null }));
                                return;
                              }
                              const val = Math.max(0, parseInt(raw, 10) || 0);
                              const msg = val > avail
                                ? `Maximum available is ${avail}`
                                : null;
                              setVariantQtys((prev) => ({ ...prev, [v.id]: val }));
                              setVariantErrors((prev) => ({ ...prev, [v.id]: msg }));
                            }}
                            className={`pos-input pos-input-sm ${errorMsg ? 'pos-input-error' : ''}`}
                            disabled={alreadySelected}
                          />
                          {alreadySelected && (
                            <div className="pos-variant-note">
                              Already in current sale — edit quantity and price on the right.
                            </div>
                          )}
                          {!alreadySelected && errorMsg && (
                            <div className="pos-variant-error">{errorMsg}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="pos-modal-actions">
              <button type="button" className="pos-btn-secondary" onClick={() => setVariantModalProduct(null)}>
                Cancel
              </button>
              <button type="button" className="pos-btn-primary" onClick={addVariantsToCart}>
                Add to sale
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice modal - single element for print */}
      {invoiceModal && (
        <div className="pos-modal-overlay pos-invoice-overlay" onClick={closeInvoiceModal}>
          <div id="pos-invoice-print-area" className="pos-modal pos-invoice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pos-invoice-actions no-print">
              <button type="button" className="pos-btn-primary" onClick={handlePrint}>
                Print
              </button>
              <button type="button" className="pos-btn-secondary" onClick={closeInvoiceModal}>
                Close
              </button>
            </div>
            <div className="invoice-top invoice-top-open">
              <div className="invoice-header-row">
                <h2 className="invoice-title">Invoice</h2>
                <span className="invoice-number">{invoiceModal.invoice_number ?? invoiceModal.invoiceNumber}</span>
              </div>
              <div className="invoice-info-grid">
                {invoiceModal.business && (
                  <div className="invoice-info-item">
                    <span className="invoice-info-label">Business</span>
                    <span className="invoice-info-value">{invoiceModal.business.name}{invoiceModal.business.address ? ` · ${invoiceModal.business.address}` : ''}</span>
                  </div>
                )}
                {invoiceModal.branch && (
                  <div className="invoice-info-item">
                    <span className="invoice-info-label">Branch</span>
                    <span className="invoice-info-value">{invoiceModal.branch.name ?? invoiceModal.branch_name ?? '—'}</span>
                  </div>
                )}
                <div className="invoice-info-item">
                  <span className="invoice-info-label">Date</span>
                  <span className="invoice-info-value">{invoiceModal.sale_date ?? invoiceModal.saleDate}</span>
                </div>
                <div className="invoice-info-item">
                  <span className="invoice-info-label">Customer</span>
                  <span className="invoice-info-value">{invoiceModal.customer_name ?? invoiceModal.customerName ?? 'Walk-in'}</span>
                </div>
                <div className="invoice-info-item">
                  <span className="invoice-info-label">Phone</span>
                  <span className="invoice-info-value">{invoiceModal.customer_phone ?? invoiceModal.customerPhone ?? '—'}</span>
                </div>
              </div>
            </div>
            <div className="invoice-items">
              <table>
                <thead>
                  <tr>
                    <th>Product Code</th>
                    <th>Product</th>
                    <th>Variant</th>
                    <th>Qty</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoiceModal.items || []).map((item, idx) => (
                    <tr key={idx}>
                      <td className="invoice-product-code">{getSaleItemProductCode(item)}</td>
                      <td>{item.product?.name ?? item.productName}</td>
                      <td>{item.variant ? [item.variant.size, item.variant.color].filter(Boolean).filter(x => String(x).toLowerCase() !== 'default').join(' / ') || '—' : '—'}</td>
                      <td>{item.quantity}</td>
                      <td>{formatPKR(item.unit_price ?? item.unitPrice)}</td>
                      <td>{formatPKR((item.quantity * (item.unit_price ?? item.unitPrice)) - (item.discount || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="invoice-summary">
              <div><span>Subtotal:</span> <span>{formatPKR(invoiceModal.subtotal)}</span></div>
              <div><span>Discount:</span> <span>{formatPKR(invoiceModal.discount)}</span></div>
              <div><span>Tax:</span> <span>{formatPKR(invoiceModal.tax)}</span></div>
              <div className="total"><span>Total:</span> <span>{formatPKR(invoiceModal.total_amount ?? invoiceModal.totalAmount)}</span></div>
            </div>
          </div>
        </div>
      )}

      {showReturnSaleModal && (
        <div className="pos-modal-overlay" onClick={() => setShowReturnSaleModal(false)}>
          <div className="pos-modal pos-return-sale-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Return sale</h3>
            <p className="pos-return-sale-hint">Select an invoice to create a return. You will complete the return on the Sales page.</p>
            <input
              type="text"
              placeholder="Search by invoice number..."
              value={returnSaleSearch}
              onChange={(e) => setReturnSaleSearch(e.target.value)}
              className="pos-input pos-return-sale-search"
              aria-label="Search by invoice number"
            />
            {returnSaleLoading ? (
              <Loader message="Loading sales…" variant="block" />
            ) : (() => {
              const q = (returnSaleSearch || '').trim().toLowerCase();
              const filtered = q
                ? returnSaleList.filter((s) => (s.invoice_number || s.invoiceNumber || '').toLowerCase().includes(q))
                : returnSaleList;
              return (
                <div className="pos-return-sale-list-wrap">
                  {filtered.length === 0 ? (
                    <p className="pos-return-sale-empty">
                      {returnSaleList.length === 0 ? 'No sales found for this branch.' : 'No invoice matches your search.'}
                    </p>
                  ) : (
                    <ul className="pos-return-sale-list">
                      {filtered.map((sale) => (
                        <li key={sale.id} className="pos-return-sale-item">
                          <div className="pos-return-sale-item-info">
                            <span className="pos-return-sale-invoice">{sale.invoice_number || sale.invoiceNumber}</span>
                            <span className="pos-return-sale-date">{new Date(sale.sale_date || sale.saleDate).toLocaleDateString()}</span>
                            <span className="pos-return-sale-customer">{sale.customer?.name || sale.customer_name || 'Walk-in'}</span>
                            <span className="pos-return-sale-total">{formatPKR(sale.total_amount ?? sale.totalAmount ?? 0)}</span>
                          </div>
                          <button
                            type="button"
                            className="pos-btn-primary pos-btn-create-return"
                            onClick={() => {
                              setShowReturnSaleModal(false);
                              navigate('/sales', { state: { openReturnForSaleId: sale.id } });
                            }}
                          >
                            Create return
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}
            <div className="pos-modal-actions">
              <button type="button" className="pos-btn-secondary" onClick={() => setShowReturnSaleModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PointOfSale;
